import React from 'react';
import {
  Box,
  Card,
  CardContent,
  IconButton,
  CircularProgress,
} from '@mui/material';
import {
  Settings as SettingsIcon,
  Edit as EditIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  CloudUpload as UploadIcon,
  DragIndicator as DragIcon,
  Image as ImageIcon,
} from '@mui/icons-material';
import { Page } from '../../../types';

interface PageSlot {
  id: string;
  row: number;
  column: string;
  page?: Page;
  isEmpty: boolean;
}

interface PageSlotCardProps {
  slot: PageSlot;
  isUploading: boolean;
  isDragging: boolean;
  onDragStart: (e: React.DragEvent, slot: PageSlot) => void;
  onDragOver: (e: React.DragEvent) => void;
  onDrop: (e: React.DragEvent, slot: PageSlot) => void;
  onDragEnd: () => void;
  onDelete: (slot: PageSlot) => void;
  onEdit: (page: Page) => void;
  onImageSelect: (slotId: string, event: React.MouseEvent<HTMLElement>) => void;
  onImageDrop: (e: React.DragEvent, slotId: string) => void;
  onImageDragOver: (e: React.DragEvent) => void;
}

export const PageSlotCard: React.FC<PageSlotCardProps> = React.memo(({
  slot,
  isUploading,
  isDragging,
  onDragStart,
  onDragOver,
  onDrop,
  onDragEnd,
  onDelete,
  onEdit,
  onImageSelect,
  onImageDrop,
  onImageDragOver,
}) => {
  return (
    <Card 
      sx={{ 
        width: 168,
        height: 292, // 168 * (1740 / 1000) = 292.32 ≈ 292 (フロント側と同じ比率)
        cursor: slot.page ? 'grab' : 'pointer',
        border: '1px solid #e0e0e0',
        position: 'relative',
        overflow: 'hidden',
        borderRadius: 1,
        boxShadow: 'none',
        transition: 'opacity 0.2s',
        opacity: isDragging ? 0.5 : isUploading ? 0.7 : 1,
        '&:active': {
          cursor: slot.page ? 'grabbing' : 'pointer'
        }
      }}
      onClick={(e) => {
        // スロット全体をクリックで画像アップロードメニューを開く
        onImageSelect(slot.id, e);
      }}
      draggable={!!slot.page}
      onDragStart={(e) => onDragStart(e, slot)}
      onDragOver={onDragOver}
      onDrop={(e) => onDrop(e, slot)}
      onDragEnd={onDragEnd}
    >
      <CardContent sx={{ p: 0, height: '100%', position: 'relative', '&:last-child': { pb: 0 } }}>
        {slot.page ? (
          <>
            {/* 背景画像 */}
            {slot.page.backgroundImage && (
              <Box
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  backgroundImage: `url(${slot.page.backgroundImage})`,
                  backgroundSize: 'cover',
                  backgroundPosition: 'center',
                }}
              />
            )}
            
            {/* ドラッグハンドル（サムネイル下部にグレー背景の帯として表示） */}
            <Box 
              sx={{
                position: 'absolute',
                bottom: 0,
                left: 0,
                right: 0,
                backgroundColor: 'rgba(128, 128, 128, 0.5)',
                backdropFilter: 'blur(8px)',
                WebkitBackdropFilter: 'blur(8px)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                py: 0.5,
                zIndex: 2,
              }}
            >
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(3, 1fr)',
                  gridTemplateRows: 'repeat(2, 1fr)',
                  gap: 0.5,
                }}
              >
                {[...Array(6)].map((_, index) => (
                  <Box
                    key={index}
                    sx={{
                      width: 4,
                      height: 4,
                      borderRadius: '50%',
                      backgroundColor: 'white',
                    }}
                  />
                ))}
              </Box>
            </Box>

            {/* hover時のオーバーレイ（霧がかかった感じ） */}
            <Box 
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: 'rgba(128, 128, 128, 0.5)',
                backdropFilter: 'blur(4px)',
                WebkitBackdropFilter: 'blur(4px)',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                opacity: 0,
                transition: 'opacity 0.2s',
                zIndex: 1,
                '.MuiCard-root:hover &': {
                  opacity: 1
                }
              }}
            >
              {/* 中央の画像アイコン */}
              <ImageIcon sx={{ fontSize: '2rem', color: 'text.secondary' }} />
            </Box>

            {/* hover時のアクションボタン（帯の上に表示、霧より上） */}
            <Box
              sx={{
                position: 'absolute',
                bottom: '24px',
                left: 0,
                right: 0,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                px: 1,
                py: 0.75,
                opacity: 0,
                transition: 'opacity 0.2s',
                zIndex: 2,
                '.MuiCard-root:hover &': {
                  opacity: 1
                }
              }}
            >
              {/* 左: 設定アイコン */}
              <IconButton 
                size="small" 
                sx={{ 
                  backgroundColor: 'rgba(128, 128, 128, 0.6)',
                  backdropFilter: 'blur(8px)',
                  WebkitBackdropFilter: 'blur(8px)',
                  color: 'white',
                  borderRadius: '8px',
                  '&:hover': {
                    backgroundColor: 'rgba(128, 128, 128, 0.7)',
                  },
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  onEdit(slot.page!);
                }}
              >
                <SettingsIcon />
              </IconButton>
              {/* 右: 削除アイコン */}
              <IconButton 
                size="small" 
                sx={{ 
                  backgroundColor: 'rgba(128, 128, 128, 0.6)',
                  backdropFilter: 'blur(8px)',
                  WebkitBackdropFilter: 'blur(8px)',
                  color: 'white',
                  borderRadius: '8px',
                  '&:hover': {
                    backgroundColor: 'rgba(128, 128, 128, 0.7)',
                  },
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  onDelete(slot);
                }}
              >
                <DeleteIcon />
              </IconButton>
            </Box>

            {/* アップロード中インジケーター */}
            {isUploading && (
              <Box 
                sx={{
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  transform: 'translate(-50%, -50%)',
                  backgroundColor: 'rgba(0, 0, 0, 0.7)',
                  borderRadius: '50%',
                  width: 32,
                  height: 32,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  zIndex: 10,
                }}
              >
                <CircularProgress size={20} sx={{ color: 'white' }} />
              </Box>
            )}
          </>
        ) : (
          // 空のスロット
          <Box
            sx={{
              height: '100%',
              position: 'relative',
              backgroundColor: '#f9f9f9',
            }}
            onDragOver={onImageDragOver}
            onDrop={(e) => onImageDrop(e, slot.id)}
          >
            {/* 中央の画像アイコン（常時表示）＋ hover時は背景だけ暗くする */}
            <Box
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                backgroundColor: 'transparent',
                transition: 'background-color 0.2s',
                '.MuiCard-root:hover &': {
                  backgroundColor: 'rgba(0, 0, 0, 0.15)',
                },
              }}
            >
              <ImageIcon sx={{ fontSize: '2rem', color: 'text.secondary' }} />
            </Box>
          </Box>
        )}
      </CardContent>
    </Card>
  );
});

PageSlotCard.displayName = 'PageSlotCard';

